import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import { useSelectContext } from "./Select";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  content: {
    position: "absolute",
    border: "1px solid #DCDCDC",
    backgroundColor: "#fff",
    zIndex: 1300, // on top of Dialog
    maxHeight: "200px",
    overflowY: "auto",
    marginTop: "4px",
    borderRadius: "4px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    padding: "4px",
    whiteSpace: "nowrap",
    "& [data-highlighted='true']": {
      backgroundColor: "#E8E8E8",
    },
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },
}));

const SelectContent = ({ children, align }) => {
  const { open, close, triggerRef, minWidth, highlightedIndex, setHighlightedIndex, searchTerm, fullHeight } = useSelectContext();
  const classes = useStyles();
  const [position, setPosition] = useState(null);
  const contentRef = useRef(null);

  const calcLeft = (rect, calculatedWidth) => {
    if (align === "right") {
      return rect.right - calculatedWidth + window.scrollX;
    }
    if (align === "left") {
      return rect.left + window.scrollX;
    }
    const wouldOverflow = rect.left + calculatedWidth > window.innerWidth;
    return wouldOverflow
      ? rect.right - calculatedWidth + window.scrollX
      : rect.left + window.scrollX;
  };

  // Measure natural content width + position content, run on every open
  // and whenever children change (e.g. async-loaded options).
  useLayoutEffect(() => {
    if (!open) return;
    if (!triggerRef.current || !contentRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();

    // Measure the content's natural width by reading scrollWidth.
    // scrollWidth reflects the unclamped content width as long as the
    // element itself isn't currently width-constrained smaller than its
    // content - so we read it BEFORE forcing any width on the element.
    const el = contentRef.current;
    const previousWidth = el.style.width;
    el.style.width = "auto"; // let it size to content
    const naturalContentWidth = el.scrollWidth;
    el.style.width = previousWidth; // restore (next render will set the real value anyway)

    const calculatedWidth = Math.max(
      rect.width,
      minWidth || 0,
      naturalContentWidth + 2 // +2 for the 1px border on each side
    );

    setPosition({
      top: rect.bottom + window.scrollY,
      left: calcLeft(rect, calculatedWidth),
      width: calculatedWidth,
    });
  }, [open, triggerRef, minWidth, children]);

  // Reposition on scroll/resize using the last-known width (cheap path,
  // no remeasure needed since content hasn't changed).
  useEffect(() => {
    if (!open) return;
    if (!triggerRef.current) return;

    const updatePosition = () => {
      if (!triggerRef.current || !contentRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const currentWidth = contentRef.current.offsetWidth || minWidth || 250;
      setPosition({
        top: rect.bottom + window.scrollY,
        left: calcLeft(rect, currentWidth),
        width: currentWidth,
      });
    };

    const handleClickOutside = (event) => {
      if (contentRef.current && !contentRef.current.contains(event.target) && !triggerRef.current.contains(event.target)) {
        close();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, close, triggerRef, minWidth]);

  // Reset highlighted index when search term changes or dropdown opens
  useEffect(() => {
    if (open) setHighlightedIndex(-1);
  }, [searchTerm]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (!contentRef.current) return;

      const items = Array.from(contentRef.current.querySelectorAll('[data-select-item]:not([data-disabled="true"])'));
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIdx = items.length === 0 ? -1 : highlightedIndex < items.length - 1 ? highlightedIndex + 1 : 0;
        setHighlightedIndex(nextIdx);
        if (items[nextIdx]) items[nextIdx].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const nextIdx = items.length === 0 ? -1 : highlightedIndex > 0 ? highlightedIndex - 1 : items.length - 1;
        setHighlightedIndex(nextIdx);
        if (items[nextIdx]) items[nextIdx].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < items.length) {
          items[highlightedIndex].click();
        }
      } else if (e.key === 'Escape' || e.key === 'Tab') {
        e.preventDefault();
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, highlightedIndex, setHighlightedIndex, close]);

  // Apply data-highlighted attribute to the correct item
  useEffect(() => {
    if (!open || !contentRef.current) return;
    const items = contentRef.current.querySelectorAll('[data-select-item]');
    items.forEach((item, i) => {
      if (i === highlightedIndex) {
        item.setAttribute('data-highlighted', 'true');
      } else {
        item.removeAttribute('data-highlighted');
      }
    });
  }, [open, highlightedIndex]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      ref={contentRef}
      className={classes.content}
      style={{
        top: position ? position.top : -9999,
        left: position ? position.left : -9999,
        width: position ? position.width : "auto",
        visibility: position ? "visible" : "hidden",
        ...(fullHeight && { maxHeight: "none", overflowY: "visible" }),
      }}
    >
      {children}
    </div>,
    document.body
  );
};

export default SelectContent;